import { Cartographic, MathUtils, Rectangle } from "../../../math";
/**
 * Internal function used for extracting needed information from tilemapresource.xml
 * @param baseUrl
 * @param document
 */
export function extractTileMapInfo(baseUrl, document) {
    const tileMap = findChildElementByName(document, "TileMap");
    if (tileMap === null)
        throw new Error("Missing <TileMap/>");
    verifyTilemapVersion(tileMap);
    verifySRS(tileMap);
    const origin = readOrigin(tileMap);
    const boundingBox = readBoundingBox(tileMap);
    const tileFormat = readTileFormat(tileMap);
    const tileSets = readTileSets(tileMap);
    return {
        origin,
        boundingBox,
        tileFormat,
        tileSets,
    };
}
function verifyTilemapVersion(tileMap) {
    const version = tileMap.attributes.getNamedItem("version");
    if (version === null)
        throw new Error("<TileMap/> missing version");
    if (version.textContent === null)
        throw new Error("<TileMap/> missing version");
    if (version.textContent !== "1.0.0")
        throw new Error("Unsupported TMS version");
}
function verifySRS(tileMap) {
    const srs = findChildElementByName(tileMap, "SRS");
    if (srs === null)
        throw new Error("Missing <SRS/>");
    if (srs.textContent === null)
        throw new Error("<SRS/> missing content");
    if (srs.textContent !== "EPSG:3857")
        throw new Error(`Unsupported SRS ${srs.textContent}`);
}
function readOrigin(tileMap) {
    // Get the bounding box tileMap
    const origin = findChildElementByName(tileMap, "Origin");
    if (origin === null)
        throw new Error("Missing <Origin/>");
    // Extract the 4 attributes
    const xAttr = origin.attributes.getNamedItem("x");
    const yAttr = origin.attributes.getNamedItem("y");
    // Check if any of the attributes were missing
    if (xAttr === null)
        throw new Error(`x attribute missing`);
    if (yAttr === null)
        throw new Error(`y attribute missing`);
    if (xAttr.textContent === null)
        throw new Error(`x attribute missing content`);
    if (yAttr.textContent === null)
        throw new Error(`y attribute missing content`);
    // Convert the text to number
    let x = Number(xAttr.textContent);
    let y = Number(xAttr.textContent);
    // Check that the text->number conversion was valid
    if (isNaN(x))
        throw new Error("Failed to parse x");
    if (isNaN(y))
        throw new Error("Failed to parse y");
    // Convert the source in degrees to radians
    x = MathUtils.degreesToRadians(x);
    y = MathUtils.degreesToRadians(y);
    return new Cartographic(x, y);
}
function readBoundingBox(tileMap) {
    // Get the bounding box tileMap
    const boundingBox = findChildElementByName(tileMap, "BoundingBox");
    if (boundingBox === null)
        throw new Error("Missing <BoundingBox/>");
    // Extract the 4 attributes
    const minXAttr = boundingBox.attributes.getNamedItem("minx");
    const minYAttr = boundingBox.attributes.getNamedItem("miny");
    const maxXAttr = boundingBox.attributes.getNamedItem("maxx");
    const maxYAttr = boundingBox.attributes.getNamedItem("maxy");
    // Check if any of the attributes were missing
    if (minXAttr === null)
        throw new Error(`minx attribute missing`);
    if (minYAttr === null)
        throw new Error(`miny attribute missing`);
    if (maxXAttr === null)
        throw new Error(`maxx attribute missing`);
    if (maxYAttr === null)
        throw new Error(`maxy attribute missing`);
    if (minXAttr.textContent === null)
        throw new Error(`minx attribute missing content`);
    if (minYAttr.textContent === null)
        throw new Error(`miny attribute missing content`);
    if (maxXAttr.textContent === null)
        throw new Error(`maxx attribute missing content`);
    if (maxYAttr.textContent === null)
        throw new Error(`maxy attribute missing content`);
    // Convert the text to number
    let minX = Number(minXAttr.textContent);
    let minY = Number(minYAttr.textContent);
    let maxX = Number(maxXAttr.textContent);
    let maxY = Number(maxYAttr.textContent);
    // Check that the text->number conversion was valid
    if (isNaN(minX))
        throw new Error("Failed to parse minX");
    if (isNaN(minY))
        throw new Error("Failed to parse minY");
    if (isNaN(maxX))
        throw new Error("Failed to parse maxX");
    if (isNaN(maxY))
        throw new Error("Failed to parse maxY");
    // Convert the source in degrees to radians
    minX = MathUtils.degreesToRadians(minX);
    minY = MathUtils.degreesToRadians(minY);
    maxX = MathUtils.degreesToRadians(maxX);
    maxY = MathUtils.degreesToRadians(maxY);
    return new Rectangle(minX, minY, maxX, maxY);
}
function readTileFormat(tileMap) {
    // Get the bounding box element
    const tileFormat = findChildElementByName(tileMap, "TileFormat");
    if (tileFormat === null)
        throw new Error("Missing <TileFormat/>");
    // Extract the 4 attributes
    const widthAttr = tileFormat.attributes.getNamedItem("width");
    const heightAttr = tileFormat.attributes.getNamedItem("height");
    const mimeTypeAttr = tileFormat.attributes.getNamedItem("mime-type");
    const extensionAttr = tileFormat.attributes.getNamedItem("extension");
    // Check if any of the attributes were missing
    if (widthAttr === null)
        throw new Error(`width attribute missing`);
    if (heightAttr === null)
        throw new Error(`height attribute missing`);
    if (mimeTypeAttr === null)
        throw new Error(`mimeType attribute missing`);
    if (extensionAttr === null)
        throw new Error(`extension attribute missing`);
    if (widthAttr.textContent === null)
        throw new Error(`width attribute missing content`);
    if (heightAttr.textContent === null)
        throw new Error(`height attribute missing content`);
    if (mimeTypeAttr.textContent === null)
        throw new Error(`mimeType attribute missing content`);
    if (extensionAttr.textContent === null)
        throw new Error(`extension attribute missing content`);
    // Convert the width and height to number
    const width = Number(widthAttr.textContent);
    const height = Number(heightAttr.textContent);
    // Check that the text->number conversion was valid
    if (isNaN(width))
        throw new Error("Failed to parse width");
    if (isNaN(height))
        throw new Error("Failed to parse height");
    return {
        width,
        height,
        extension: extensionAttr.textContent,
        mimeType: mimeTypeAttr.textContent,
    };
}
function readTileSets(tileMap) {
    // Get the bounding box element
    const tileSets = findChildElementByName(tileMap, "TileSets");
    if (tileSets === null)
        throw new Error("Missing <TileSets/>");
    // Get the tile set profile
    const profileAttr = tileSets.attributes.getNamedItem("profile");
    if (profileAttr === null)
        throw new Error(`profile attribute missing`);
    if (profileAttr.textContent === null)
        throw new Error(`profile attribute missing content`);
    if (profileAttr.textContent !== "mercator")
        throw new Error(`Unsupported TileSet profile ${profileAttr.textContent}`);
    const zoomHrefMap = new Map();
    let minLevel = 512;
    let maxLevel = 0;
    for (let i = 0; i < tileSets.children.length; i += 1) {
        const tileSet = tileSets.children.item(i);
        if (tileSet === null)
            throw new Error("Failed to get <TileSet/>");
        if (tileSet.tagName !== "TileSet")
            continue;
        // Get the needed attributes
        const orderAttr = tileSet.attributes.getNamedItem("order");
        const hrefAttr = tileSet.attributes.getNamedItem("href");
        // Check the attributes are valid
        if (orderAttr === null)
            throw new Error(`order attribute missing`);
        if (hrefAttr === null)
            throw new Error(`href attribute missing`);
        if (orderAttr.textContent === null)
            throw new Error(`order attribute missing content`);
        if (hrefAttr.textContent === null)
            throw new Error(`href attribute missing content`);
        // Parse the zoom level / order out
        const order = Number(orderAttr.textContent);
        if (isNaN(order))
            throw new Error("Failed to parse order");
        // Update the min and max level
        minLevel = Math.min(order, minLevel);
        maxLevel = Math.max(order, maxLevel);
        zoomHrefMap.set(order, hrefAttr.textContent);
    }
    return {
        maxLevel,
        minLevel,
        zoomHrefMap,
    };
}
function findChildElementByName(parent, name) {
    for (let i = 0; i < parent.children.length; i += 1) {
        const child = parent.children.item(i);
        if (child === null)
            continue;
        if (child.tagName === name)
            return child;
    }
    return null;
}
